import React, { useState, useCallback, useMemo, memo } from 'react';
import { connect } from 'react-redux';

import connectOptions, { mergeProps } from '../../utils/connectOptions';
import {
  createEmailTemplates,
  saveEmailTemplates,
  deleteEmailTemplates,
  clearValidationError,
} from '../../actions/emailTemplates';
import Popups from '../../components/Popup';
import stopPropagation from '../../decorators/stopPropagation';
import confirm from '../decorators/confirm';
import { PrettyErrorProvider } from '../../contexts/PrettyErrorContext';
import { usePopupContext } from '../../contexts/PopupContext';

function isChanged(original, changed) {
  return original !== changed;
}

const AddEmailTemplatePopupContainer = ({
  children,
  popup,
  inputErrors,
  createEmailTemplates,
  saveEmailTemplates,
  deleteEmailTemplates,
  clearValidationError,
}) => {
  const context = usePopupContext();
  const [submitting, setSubmitting] = useState(false);
  const [values, setValues] = useState({
    label: popup.getIn(['props', 'emailTemplate', 'label'], ''),
    subject: popup.getIn(['props', 'emailTemplate', 'subject'], ''),
    body: popup.getIn(['props', 'emailTemplate', 'body'], ''),
  });
  const [changed, setChanged] = useState({
    label: false,
    subject: false,
    body: false,
  });

  const handleChange = useCallback(
    event => {
      const { name, value } = event.target;

      clearValidationError({ field: name });

      switch (name) {
        case 'label':
          setValues(prev => ({ ...prev, label: value }));
          setChanged(prev => ({
            ...prev,
            label: isChanged(popup.getIn(['props', 'emailTemplate', 'label'], ''), value),
          }));
          break;

        case 'subject':
          setValues(prev => ({ ...prev, subject: value }));
          setChanged(prev => ({
            ...prev,
            subject: isChanged(popup.getIn(['props', 'emailTemplate', 'subject'], ''), value),
          }));
          break;

        default:
          setValues(prev => ({ ...prev, body: value }));
          setChanged(prev => ({
            ...prev,
            body: isChanged(popup.getIn(['props', 'emailTemplate', 'body'], ''), value),
          }));
          break;
      }
    },
    [popup, clearValidationError],
  );

  const handleClose = useCallback(() => {
    context.closePopup();
  }, [context]);

  const handleCreate = useCallback(() => {
    const { label, subject, body } = values;
    const type = popup.getIn(['props', 'templateType'], '');
    const templateId = popup.getIn(['props', 'emailTemplate', 'id']);

    const afterError = () => {
      setSubmitting(false);
    };

    setSubmitting(true);

    if (templateId) {
      saveEmailTemplates({
        id: templateId,
        userId: popup.getIn(['props', 'userId']),
        body: { label, subject, body, type },
        afterSuccess: handleClose,
        afterError,
      });
    } else {
      createEmailTemplates({
        userId: popup.getIn(['props', 'userId']),
        body: { label, subject, body, type },
        afterSuccess: handleClose,
        afterError,
      });
    }
  }, [values, popup, saveEmailTemplates, createEmailTemplates, handleClose]);

  const handleDeleteFn = useCallback(() => {
    deleteEmailTemplates({
      id: popup.getIn(['props', 'emailTemplate', 'id']),
      userId: popup.getIn(['props', 'userId']),
      afterSuccess: handleClose,
    });
  }, [popup, deleteEmailTemplates, handleClose]);

  const handleDelete = useMemo(() => stopPropagation(confirm('Delete this email template?', handleDeleteFn, context)), [
    handleDeleteFn,
    context,
  ]);

  const isValid = inputErrors && inputErrors.size === 0;

  return (
    <PrettyErrorProvider inputErrors={inputErrors}>
      <>
        <Popups.AddEmailTemplatePopup
          changed={changed}
          emailTemplate={popup.getIn(['props', 'emailTemplate'])}
          headerTitle={popup.getIn(['props', 'headerTitle'])}
          isNew={popup.getIn(['props', 'isNew'])}
          isValid={isValid}
          onChange={handleChange}
          onClose={handleClose}
          onCreate={handleCreate}
          onDelete={handleDelete}
          submitting={submitting}
          values={values}
        />
        {children}
      </>
    </PrettyErrorProvider>
  );
};

function mapStateToProps(state, props) {
  return {
    ...props,
    inputErrors: state.emailTemplates.get('inputErrors'),
  };
}

export { AddEmailTemplatePopupContainer };

export default connect(
  mapStateToProps,
  {
    createEmailTemplates,
    saveEmailTemplates,
    deleteEmailTemplates,
    clearValidationError,
  },
  mergeProps,
  connectOptions,
)(memo(AddEmailTemplatePopupContainer));
